<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">自動回應成效</h1>
      </div>
    </div>

    <div class="page-container">
    <!-- 時間篩選 -->
    <div class="page-toolbar">
      <div class="filter-group">
        <label>開始日期</label>
        <input
          v-model="filters.startDate"
          type="date"
          class="form-control"
        />
      </div>
      <div class="filter-group">
        <label>結束日期</label>
        <input
          v-model="filters.endDate"
          type="date"
          class="form-control"
        />
      </div>
      <button class="btn btn-primary" @click="loadStatistics">查詢</button>
      <button class="btn btn-secondary" @click="resetFilters">重置</button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else class="statistics-content">
      <!-- 統計卡片 -->
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-label">總回覆數</div>
          <div class="stat-value">{{ statistics.totalReplies || 0 }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">未匹配訊息數</div>
          <div class="stat-value">{{ statistics.totalUnmatched || 0 }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">回覆成功率</div>
          <div class="stat-value">
            {{ statistics.totalReplies > 0 
              ? ((statistics.totalReplies / (statistics.totalReplies + statistics.totalUnmatched)) * 100).toFixed(1)
              : 0 }}%
          </div>
        </div>
      </div>

      <!-- 規則命中率統計 -->
      <div class="section-card">
        <h3 class="section-title">規則命中率統計（Top 10）</h3>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>規則名稱</th>
                <th>命中次數</th>
                <th>日誌記錄數</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="statistics.ruleStats && statistics.ruleStats.length === 0">
                <td colspan="4" class="empty-state">暫無資料</td>
              </tr>
              <tr v-for="rule in statistics.ruleStats" :key="rule.id">
                <td>{{ rule.ruleName }}</td>
                <td class="text-center">{{ rule.hitCount || 0 }}</td>
                <td class="text-center">{{ rule.logCount || 0 }}</td>
                <td>
                  <button
                    class="btn btn-sm btn-primary"
                    @click="viewRuleDetail(rule.id)"
                  >
                    查看詳情
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 未匹配訊息列表 -->
      <div class="section-card">
        <h3 class="section-title">未匹配訊息列表</h3>
        <div class="page-toolbar">
          <div class="search-box">
            <input
              v-model="unmatchedSearchForm.search"
              type="text"
              class="form-control"
              placeholder="搜尋訊息內容..."
              @input="handleUnmatchedSearch"
            />
            <i class="ri-search-line"></i>
          </div>
          <div class="filter-group">
            <select
              v-model="unmatchedSearchForm.processed"
              class="form-control"
              @change="handleUnmatchedSearch"
            >
              <option value="">全部狀態</option>
              <option :value="false">未處理</option>
              <option :value="true">已處理</option>
            </select>
          </div>
        </div>

        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>訊息內容</th>
                <th>訊息類型</th>
                <th>狀態</th>
                <th>時間</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="unmatchedMessages.length === 0">
                <td colspan="5" class="empty-state">暫無未匹配訊息</td>
              </tr>
              <tr v-for="message in unmatchedMessages" :key="message.id">
                <td>{{ message.messageText }}</td>
                <td>
                  <span class="badge badge-info">
                    {{ message.messageType || 'text' }}
                  </span>
                </td>
                <td>
                  <span
                    class="badge"
                    :class="message.processed ? 'badge-success' : 'badge-warning'"
                  >
                    {{ message.processed ? '已處理' : '未處理' }}
                  </span>
                </td>
                <td>{{ formatDate(message.createdAt) }}</td>
                <td>
                  <button
                    v-if="!message.processed"
                    class="btn btn-sm btn-primary"
                    @click="handleProcessMessage(message.id)"
                  >
                    標記為已處理
                  </button>
                  <span v-else class="text-muted">已處理</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 未匹配訊息分頁 -->
        <ListPagination
          v-if="unmatchedPagination.totalPages > 1"
          :page="unmatchedPagination.page"
          :total-pages="unmatchedPagination.totalPages"
          :total="unmatchedPagination.total"
          @change="handleUnmatchedPageChange"
        />
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { autoReplyService } from '@/api/modules/autoReply.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'AutoReplyStatistics',
  data() {
    return {
      loading: false,
      filters: {
        startDate: '',
        endDate: ''
      },
      statistics: {
        ruleStats: [],
        totalUnmatched: 0,
        totalReplies: 0
      },
      unmatchedMessages: [],
      unmatchedPagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        totalPages: 0
      },
      unmatchedSearchForm: {
        search: '',
        processed: ''
      }
    };
  },
  mounted() {
    this.loadStatistics();
    this.loadUnmatchedMessages();
  },
  methods: {
    async loadStatistics() {
      this.loading = true;
      try {
        const params = {};
        if (this.filters.startDate) {
          params.startDate = this.filters.startDate;
        }
        if (this.filters.endDate) {
          params.endDate = this.filters.endDate;
        }
        const response = await autoReplyService.getStatistics(params);
        this.statistics = response.data || {
          ruleStats: [],
          totalUnmatched: 0,
          totalReplies: 0
        };
      } catch (error) {
        console.error('載入統計資料失敗:', error);
        showError('載入統計資料失敗', '');
      } finally {
        this.loading = false;
      }
    },

    async loadUnmatchedMessages() {
      try {
        // 過濾掉空值參數
        const params = {
          page: this.unmatchedPagination.page,
          pageSize: this.unmatchedPagination.pageSize
        };
        
        // 只添加非空值參數
        if (this.unmatchedSearchForm.search && this.unmatchedSearchForm.search.trim()) {
          params.search = this.unmatchedSearchForm.search.trim();
        }
        
        if (this.unmatchedSearchForm.processed !== '' && this.unmatchedSearchForm.processed !== null && this.unmatchedSearchForm.processed !== undefined) {
          params.processed = this.unmatchedSearchForm.processed;
        }
        
        const response = await autoReplyService.listUnmatchedMessages(params);
        this.unmatchedMessages = response.data.items || [];
        const pg = response.data.pagination || {};
        this.unmatchedPagination = {
          ...this.unmatchedPagination,
          page: pg.page || this.unmatchedPagination.page,
          pageSize: pg.pageSize || this.unmatchedPagination.pageSize,
          total: pg.totalItems ?? pg.total ?? 0,
          totalPages: pg.totalPages || 0
        };
      } catch (error) {
        console.error('載入未匹配訊息失敗:', error);
        showError('載入未匹配訊息失敗', '');
      }
    },

    handleUnmatchedSearch() {
      this.unmatchedPagination.page = 1;
      this.loadUnmatchedMessages();
    },

    handleUnmatchedPageChange(page) {
      this.unmatchedPagination.page = page;
      this.loadUnmatchedMessages();
    },

    async handleProcessMessage(id) {
      try {
        await autoReplyService.processUnmatchedMessage(id);
        await showSuccess('標記成功', '');
        this.loadUnmatchedMessages();
        // 重新載入統計資料
        this.loadStatistics();
      } catch (error) {
        console.error('標記失敗:', error);
        showError('標記失敗', '');
      }
    },

    resetFilters() {
      this.filters = {
        startDate: '',
        endDate: ''
      };
      this.loadStatistics();
    },

    viewRuleDetail(ruleId) {
      this.$router.push({
        name: 'AutoReplyRules',
        query: { highlight: ruleId }
      });
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString('zh-TW');
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-toolbar {
  align-items: flex-end;
}

.statistics-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 32px;
  font-weight: 600;
  color: #2196f3;
}

.section-card {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge-info {
  background-color: #e3f2fd;
  color: #1976d2;
}

.badge-success {
  background-color: #e8f5e9;
  color: #4caf50;
}

.badge-warning {
  background-color: #fff3e0;
  color: #ff9800;
}

.text-muted {
  color: #999;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #2196f3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
